import React, { useCallback, useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import axios from 'axios';
import { mensajeDeError } from '../utils/errores';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/legal`;

/**
 * Avisa qué documentos legales le faltan aceptar a la ferretería.
 *
 * No se puede cerrar, a diferencia del aviso del timbrado. Ese informa de algo
 * que va a pasar y conviene poder postergarlo; este informa de algo que falta y
 * que nadie más va a hacer. Un aviso que se oculta con una cruz es un aviso que
 * se oculta la primera vez y no vuelve a verse.
 *
 * Tampoco bloquea el sistema. Una ferretería no puede quedarse sin poder vender
 * porque falte una aceptación: el mostrador no espera. El aviso queda hasta que
 * se resuelva.
 */
export default function AvisoLegal() {
    const [estado, setEstado] = useState(undefined);  // undefined = cargando
    const [guardando, setGuardando] = useState('');
    const [error, setError] = useState('');

    const consultar = useCallback(() => {
        axios.get(`${API}/aceptacion/`)
            .then(r => setEstado(r.data))
            // Si la consulta falla no se inventa un estado: afirmar que falta
            // aceptar algo cuando no se sabe sería peor que no decir nada.
            .catch(() => setEstado(undefined));
    }, []);

    useEffect(consultar, [consultar]);

    const aceptar = (clave, version) => {
        setGuardando(clave);
        setError('');
        axios.post(`${API}/aceptacion/`, { documento: clave, version })
            .then(() => consultar())
            .catch(e => setError(mensajeDeError(e, 'No se pudo registrar la aceptación.')))
            .finally(() => setGuardando(''));
    };

    if (estado === undefined || estado.al_dia) return null;

    const rutas = { terminos: '/terminos', privacidad: '/privacidad' };

    return (
        <div className="mx-8 mt-4 px-4 py-3 rounded-xl border text-sm bg-alerta-50 border-alerta-200 dark:bg-alerta-950/40 dark:border-alerta-800 text-alerta-800 dark:text-alerta-300">
            <div className="flex items-start gap-3">
                <FileText size={16} className="text-alerta-500 shrink-0 mt-0.5" />
                <div className="flex-1">
                    <p className="font-bold">
                        {estado.puede_aceptar
                            ? 'Falta aceptar los documentos del servicio'
                            : 'La ferretería todavía no aceptó los documentos del servicio'}
                    </p>
                    <p className="mt-0.5">
                        {estado.puede_aceptar
                            ? 'Leelos antes de aceptar. Son los que regulan el uso del sistema y el tratamiento de los datos que cargás.'
                            : 'Los tiene que aceptar un Administrador. Podés leerlos igual.'}
                    </p>

                    <div className="mt-3 flex flex-col gap-2">
                        {estado.pendientes.map(doc => (
                            <div key={doc.clave} className="flex flex-wrap items-center gap-3">
                                {/* Un ancla y no un Link del router: abre en
                                    otra pestaña, asi que la navegacion la hace
                                    el navegador igual, y el usuario no pierde
                                    de vista el aviso que esta por aceptar. */}
                                <a
                                    href={rutas[doc.clave] || '/terminos'}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-bold underline"
                                >
                                    {doc.titulo}
                                </a>
                                <span className="text-xs opacity-75">versión {doc.version}</span>
                                {estado.puede_aceptar && (
                                    <button
                                        type="button"
                                        onClick={() => aceptar(doc.clave, doc.version)}
                                        disabled={guardando === doc.clave}
                                        className="px-3 py-1 rounded-lg bg-alerta-500 text-negro-950 font-bold text-xs disabled:opacity-60"
                                    >
                                        {guardando === doc.clave ? 'Registrando…' : 'Acepto'}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    {error && <p className="mt-2 font-semibold text-error-700 dark:text-error-400">{error}</p>}
                </div>
            </div>
        </div>
    );
}
